import { CustomerRequestResponse } from '../services/api';

// Types
export type CurrentTabRow = CustomerRequestResponse | FlattenedFeedbackResponse | Record<string, unknown>;

export interface FlattenedFeedbackResponse {
  /** Stable row id (feedback response UUID) */
  responseId: string;
  /** Parent CustomerRequest id for API retry */
  parentRequestId: string;
  requestNumber: string | null;
  date: string;
  projectCode: string | null;
  projectName: string | null;
  projectOwner: string | null;
  customerName: string | null;
  customerEmail: string | null;
  account: string | null;
  region: string | null;
  overallSatisfaction: string | null;
  feedbackSentiment: string;
  deliveryCompletion: string | null;
  deliveryTimeline: string | null;
  deliveryClosure: string | null;
  teamTechnical: string | null;
  teamSupport: string | null;
  teamResponsiveness: string | null;
  specificContributions: string | null;
  areasForImprovement: string | null;
  zohoStatus: string | null;
  zohoIssueLink: string | null;
  zohoIssueId: string | null;
}

export interface ColumnConfig {
  id: string;
  label: string;
  field: (req: CurrentTabRow) => string | null;
  highlighted?: boolean;
  isDate?: boolean;
}

// Helper to format date in Searce standard format (DD-MMM-YY)
export const formatDateEnhanced = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return '-';
  }
};

// Helper to extract part after pipe from project_account_bu (e.g., "REGION | APAC" -> "APAC")
export const extractProjectAccountBU = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parts = value.split('|');
  return parts.length > 1 ? parts[1].trim() : value.trim();
};

// Helper to map status to display status
export const mapStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'pending': 'Pending',
    'email_sent': 'Email Sent',
    'email_failed': 'Email Failed',
    'responded': 'Responded',
    'closed': 'Closed',
    'draft': 'Draft',
  };
  return statusMap[status.toLowerCase()] || status;
};

// Helper to get feedback type from response
export const getLatestFeedbackResponse = (request: CustomerRequestResponse) =>
  request.feedback_responses && request.feedback_responses.length > 0 ? request.feedback_responses[0] : undefined;

export const getLatestFeedbackRequestByProject = (requests: CustomerRequestResponse[]) => {
  const map = new Map<string, CustomerRequestResponse>();
  requests.forEach((req) => {
    if (!req.project_code) return;
    const key = req.project_code.trim();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, req);
      return;
    }
    const existingDate = new Date(existing.created_at).getTime();
    const currentDate = new Date(req.created_at).getTime();
    if (currentDate > existingDate) {
      map.set(key, req);
    }
  });
  return map;
};

export const getFeedbackStatusLabel = (request?: CustomerRequestResponse) => {
  if (!request) return 'Not Raised';
  if (request.status === 'responded') return 'Received';
  if (request.status === 'email_failed') return 'Email Failed';
  if (request.status === 'email_sent' || request.status === 'pending') return 'Pending';
  return request.status ? request.status.toString() : 'Pending';
};

/** No solution story in Postgres for this project (enriched as "Pending" on user-projects API). */
export const isSolutionStoryAbsent = (status?: string | null): boolean =>
  !status || status === 'Pending';

/**
 * My Projects ACTION column: enable Create Request when sign-off (feedback) is missing
 * or solution story is missing. Disabled only when both exist.
 */
export const canCreateMyProjectRequest = (
  hasFeedbackRequest: boolean,
  solutionStoryStatus?: string | null
): boolean => !hasFeedbackRequest || isSolutionStoryAbsent(solutionStoryStatus);

export const getMyProjectActionLabel = (canCreate: boolean): string =>
  canCreate ? 'Create Request' : 'Complete';

export const getFeedbackType = (request: CustomerRequestResponse): string => {
  const latestResponse = getLatestFeedbackResponse(request);
  if (latestResponse?.overall_satisfaction) {
    const satisfaction = latestResponse.overall_satisfaction.toLowerCase();
    // Check for negative first (since "dissatisfied" contains "satisfied")
    if (satisfaction.includes('highly dissatisfied') || satisfaction.includes('dissatisfied')) {
      return 'Negative';
    } else if (satisfaction.includes('highly satisfied') || satisfaction.includes('satisfied') || satisfaction === 'neutral') {
      return 'Positive';
    }
    return 'Positive'; // Default to Positive for any other value
  }
  // If no response yet, check status
  if (request.status === 'responded') return 'Pending Review';
  return 'Pending';
};

// Helper to get Customer Response Status
export const getCustomerResponseStatus = (request: CustomerRequestResponse): string => {
  const latestResponse = getLatestFeedbackResponse(request);
  // If customer has submitted feedback
  if (latestResponse?.submitted_at) {
    return 'Received';
  }
  // If email was sent, customer is awaiting response
  if (request.feedback_detail?.email_sent_at) {
    return 'Awaiting';
  }
  // Default: awaiting customer response (email not sent yet)
  return 'Awaiting';
};

// Helper to get Email Status
export const getEmailStatus = (request: CustomerRequestResponse): string => {
  // Check if email was sent successfully
  if (request.feedback_detail?.email_sent_at && !request.feedback_detail?.email_error) {
    return 'Sent';
  }
  // Check if email failed
  if (request.feedback_detail?.email_error) {
    return 'Failed';
  }
  // Email not sent yet
  return 'Not Sent';
};

// Helper to get Zoho Status (only used for Feedback and Escalation tables)
export const getZohoStatus = (request: CustomerRequestResponse): string => {
  // For escalations, check escalation_detail for zoho_status
  if (request.request_type === 'escalation' && request.escalation_detail?.zoho_status) {
    return request.escalation_detail.zoho_status;
  }
  // For feedback, zoho_status is calculated overall from all feedback_responses
  // If any response has "Open" status, overall status is "Open"
  // Otherwise returns highest priority status (In Progress > To be tested > Closed > Failed > N/A)
  return request.zoho_status || 'N/A';
};

/** Escalation resolution closed: DB `resolution_status` closed, else legacy `date_resolved`. */
export const isEscalationResolutionClosed = (request: CustomerRequestResponse): boolean => {
  const rs = request.escalation_detail?.resolution_status?.trim().toLowerCase();
  if (rs === 'closed') return true;
  if (rs === 'open') return false;
  return Boolean(request.escalation_detail?.date_resolved);
};

/** STATUS column for escalations: Open vs Resolved (matches prior UX). */
export const getEscalationResolutionStatusLabel = (
  request: CustomerRequestResponse
): 'Open' | 'Resolved' => (isEscalationResolutionClosed(request) ? 'Resolved' : 'Open');
